import { useCallback, useEffect, useState } from "react";
import { Check, FlaskConical, LayoutGrid } from "lucide-react";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/constants";

const COLORS = [
  { name: "Azure", value: "#3B82F6" },
  { name: "Emerald", value: "#10B981" },
  { name: "Amber", value: "#F59E0B" },
  { name: "Violet", value: "#8B5CF6" },
  { name: "Rose", value: "#F43F5E" },
];

const FOCUS = {
  "Lead": "Qualification outreach",
  "FTD": "First-deposit conversion",
  "KYC": "KYC completion guidance",
  "Funded": "Funding recovery",
  "Active Trader": "Engagement monitoring",
  "Retention Risk": "Save-play re-engagement",
  "Dormant": "Reactivation sequence",
  "Withdrawal": "Withdrawal coordination — human-led",
};

const APPROVAL_LABELS = {
  not_started: "—",
  not_required: "Auto-approved",
  pending_human: "Awaiting approval",
  approved: "Approved",
};

const loadConfig = () => {
  try {
    return JSON.parse(localStorage.getItem("onet_partner_config")) || { name: "Partner CRM", monogram: "PC", color: "#3B82F6" };
  } catch {
    return { name: "Partner CRM", monogram: "PC", color: "#3B82F6" };
  }
};

export default function WhiteLabel() {
  const [config, setConfig] = useState(loadConfig);
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);

  const loadData = useCallback(() => {
    api.get("/dashboard/metrics").then((r) => setData(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data.slice(0, 8))).catch(() => {});
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    localStorage.setItem("onet_partner_config", JSON.stringify(config));
  }, [config]);

  const c = config.color;
  const m = data?.metrics;

  return (
    <div className="space-y-6" data-testid="whitelabel-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">White-Label Preview</h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
          The O.N.E.Tech intelligence engine rendered inside a neutral partner-CRM interface. Configure the partner
          name, monogram logo and primary colour — the technology operates behind the interface.
        </p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 flex flex-wrap items-end gap-4" data-testid="whitelabel-config">
        <div>
          <label className="block text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5" htmlFor="partner-name">Partner name</label>
          <input
            id="partner-name"
            data-testid="whitelabel-partner-name-input"
            value={config.name}
            onChange={(e) => setConfig({ ...config, name: e.target.value.slice(0, 30) })}
            className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-emerald-500 transition-colors duration-200 w-56"
          />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5" htmlFor="partner-monogram">Logo monogram</label>
          <input
            id="partner-monogram"
            data-testid="whitelabel-monogram-input"
            value={config.monogram}
            onChange={(e) => setConfig({ ...config, monogram: e.target.value.slice(0, 3).toUpperCase() })}
            className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-emerald-500 transition-colors duration-200 w-24 font-mono"
          />
        </div>
        <div>
          <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Primary colour</div>
          <div className="flex gap-2">
            {COLORS.map((col) => (
              <button
                key={col.value}
                data-testid={`whitelabel-color-${col.name.toLowerCase()}`}
                onClick={() => setConfig({ ...config, color: col.value })}
                title={col.name}
                className="w-8 h-8 rounded-sm border-2 transition-transform duration-150 hover:scale-110 flex items-center justify-center"
                style={{ backgroundColor: col.value, borderColor: config.color === col.value ? "#f4f4f5" : "transparent" }}
              >
                {config.color === col.value && <Check className="w-4 h-4 text-zinc-950" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-zinc-700 rounded-md overflow-hidden" data-testid="whitelabel-preview">
        <div className="px-5 py-3 flex items-center gap-3 border-b border-zinc-800" style={{ backgroundColor: "#101013" }}>
          <div className="w-9 h-9 rounded-sm flex items-center justify-center font-display font-bold text-sm text-white" style={{ backgroundColor: c }}>
            {config.monogram || "PC"}
          </div>
          <div>
            <div className="font-display font-bold text-zinc-100 text-sm leading-none">{config.name || "Partner CRM"}</div>
            <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1"><LayoutGrid className="w-3 h-3" /> Workspace · Client Intelligence</div>
          </div>
          <span className="ml-auto text-[10px] font-mono px-2.5 py-1 rounded-full border" style={{ color: c, borderColor: `${c}66`, backgroundColor: `${c}14` }}>
            Intelligence module active
          </span>
        </div>

        <div className="p-5 bg-zinc-950 space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "Prioritised clients", value: m ? m.total_action : "—" },
              { label: "Revenue at risk", value: m ? fmtMoney(m.revenue_at_risk) : "—" },
              { label: "Pending approvals", value: m ? m.pending_approvals : "—" },
              { label: "Recovered outcomes", value: m ? `${m.recovered_count} · ${fmtMoney(m.recovered_value)}` : "—" },
            ].map((k, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid={`whitelabel-kpi-${i}`}>
                <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">{k.label}</div>
                <div className="text-xl font-bold font-mono mt-1.5" style={{ color: c }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-x-auto">
            <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
              <span className="text-sm font-semibold text-zinc-200">Intelligence worklist</span>
              <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Prioritised · next-best-action · approvals · escalations</span>
            </div>
            <table className="w-full text-sm min-w-[860px]" data-testid="whitelabel-worklist">
              <thead>
                <tr className="border-b border-zinc-800 text-left">
                  <th className="px-5 py-2.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Client</th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Stage</th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Priority</th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Recommended focus</th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Approval</th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Escalation</th>
                  <th className="px-5 py-2.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Est. value</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((cl) => (
                  <tr key={cl.id} className="border-b border-zinc-800/60 hover:bg-zinc-800/40 transition-colors duration-150" data-testid={`whitelabel-row-${cl.id}`}>
                    <td className="px-5 py-2.5">
                      <div className="font-semibold text-zinc-100 text-xs">{cl.name}</div>
                      <div className="text-[10px] font-mono text-zinc-500">{cl.id}</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-zinc-300">{cl.stage}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs font-semibold" style={{ color: c }}>{cl.score}</td>
                    <td className="px-4 py-2.5 text-xs text-zinc-300">{FOCUS[cl.stage] || "Monitoring"}</td>
                    <td className="px-4 py-2.5 text-xs">
                      <span className={cl.workflow.approval === "pending_human" ? "font-semibold" : "text-zinc-500"} style={cl.workflow.approval === "pending_human" ? { color: c } : {}}>
                        {APPROVAL_LABELS[cl.workflow.approval]}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-zinc-400">{cl.escalation === "none" ? "—" : cl.escalation === "level1" ? "Level 1" : "Level 2"}</td>
                    <td className="px-5 py-2.5 text-right font-mono text-xs text-zinc-200">{fmtMoney(cl.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-[10px] font-mono text-zinc-500 border-t border-zinc-800 pt-4">
            <span className="flex items-center gap-1.5">
              <FlaskConical className="w-3 h-3" style={{ color: c }} />
              Synthetic demonstration environment — all data and connections simulated
            </span>
            <span>Intelligence engine: O.N.E.Tech technology layer (white-labelled)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
